import { useState } from 'react';
import { PageHeader } from '../components/AppShell';
import { api, ApiError } from '../lib/api';
import { amount, dateTime, quantity, unitLabel } from '../lib/format';
import { parseReturnQuantity } from '../lib/returns';
import { tr, useT, type MessageKey } from '../i18n';

/**
 * Returns against an invoice.
 *
 * Find the bill, choose what is coming back and why, and refund exactly what the server
 * computes from the stored line amounts (never today's price). Damaged or expired goods are
 * written off instead of going back on the shelf.
 */

interface SaleItem {
  id: string;
  productName: string;
  unit: string;
  qtyMilli: number;
  returnedQtyMilli: number;
  totalPaise: number;
}

interface InvoiceLookup {
  sale: { invoiceNumber: string; grandTotalPaise: number; createdAt?: string; completedAt?: string; customerName?: string | null };
  items: SaleItem[];
}

export interface ReturnResult {
  returnNumber: string;
  refundTotalPaise: number;
  refundTaxPaise: number;
  refundPayablePaise: number;
  cashRefundPaise: number;
  debtReductionPaise: number;
}

/** `label` is what the return record stores (English, so reports read the same in any language); `key` is what the screen shows. */
const REASONS = [
  { value: 'CustomerReturn', label: 'Customer changed mind', key: 'returns.reason.CustomerReturn', restock: true },
  { value: 'WrongProduct', label: 'Wrong product', key: 'returns.reason.WrongProduct', restock: true },
  { value: 'BillingMistake', label: 'Billing mistake', key: 'returns.reason.BillingMistake', restock: true },
  { value: 'Damaged', label: 'Damaged (write off)', key: 'returns.reason.Damaged', restock: false },
  { value: 'Expired', label: 'Expired (write off)', key: 'returns.reason.Expired', restock: false },
  { value: 'Other', label: 'Other', key: 'returns.reason.Other', restock: true },
] as const satisfies readonly { value: string; label: string; key: MessageKey; restock: boolean }[];

type Reason = (typeof REASONS)[number]['value'];

export function ReturnsPage() {
  const t = useT();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoice, setInvoice] = useState<InvoiceLookup | null>(null);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [reason, setReason] = useState<Reason>('CustomerReturn');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ReturnResult | null>(null);

  const lookup = async () => {
    const number = invoiceNumber.trim().toUpperCase();
    if (busy || !number) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const found = await api.get<InvoiceLookup>(`/api/sales/invoice/${encodeURIComponent(number)}`);
      setInvoice(found);
      setQty({});
    } catch (err) {
      setInvoice(null);
      setError(err instanceof ApiError ? err.message : tr('returns.lookupFailed'));
    } finally {
      setBusy(false);
    }
  };

  const remaining = (item: SaleItem) => Math.max(0, item.qtyMilli - item.returnedQtyMilli);
  const chosen = (invoice?.items ?? [])
    .map((item) => ({ item, milli: parseReturnQuantity(qty[item.id] ?? '') }))
    .filter((entry): entry is { item: SaleItem; milli: number } => entry.milli !== null && entry.milli > 0);
  const invalidQty = (invoice?.items ?? []).some((item) => parseReturnQuantity(qty[item.id] ?? '') === null);
  const tooMany = chosen.some((entry) => entry.milli > remaining(entry.item));
  // Estimate only; the server computes the exact refund from stored tax and discounts.
  const estimate = chosen.reduce(
    (sum, entry) => sum + Math.round((entry.item.totalPaise * entry.milli) / entry.item.qtyMilli),
    0,
  );

  const submit = async () => {
    if (busy || !invoice || chosen.length === 0 || tooMany || invalidQty) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<ReturnResult>('/api/sales/returns', {
        invoiceNumber: invoice.sale.invoiceNumber,
        reason: REASONS.find((r) => r.value === reason)?.label ?? reason,
        items: chosen.map((entry) => ({ saleItemId: entry.item.id, qtyMilli: entry.milli, reason })),
      });
      setDone(result);
      setInvoice(null);
      setInvoiceNumber('');
      setQty({});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('returns.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('returns.title')} subtitle={t('returns.subtitle')} />
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <div className="mx-auto max-w-3xl space-y-4">
          <form
            className="panel flex flex-wrap items-end gap-3 p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void lookup();
            }}
          >
            <label className="min-w-[220px] flex-1">
              <span className="mb-1 block text-sm font-medium text-slate-700">{t('returns.invoice')}</span>
              <input
                className="field tabular"
                data-testid="returns-invoice"
                disabled={busy}
                placeholder="INV2026-000001"
                value={invoiceNumber}
                autoFocus
                onChange={(event) => setInvoiceNumber(event.target.value)}
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="returns-find"
              disabled={busy || !invoiceNumber.trim()}
            >
              {t('returns.find')}
            </button>
          </form>

          {error ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

          {done ? <ReturnConfirmation result={done} /> : null}

          {invoice ? (
            <div className="panel overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                <div>
                  <p className="font-semibold text-slate-900">{invoice.sale.invoiceNumber}</p>
                  <p className="text-xs text-slate-500">
                    {dateTime(invoice.sale.completedAt ?? invoice.sale.createdAt ?? null)}
                    {invoice.sale.customerName ? ` · ${invoice.sale.customerName}` : ''}
                  </p>
                </div>
                <p className="tabular text-sm text-slate-600">{t('returns.bill', { amount: amount(invoice.sale.grandTotalPaise) })}</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2">{t('returns.item')}</th>
                    <th className="px-4 py-2 text-right">{t('returns.returnable')}</th>
                    <th className="px-4 py-2 text-right">{t('returns.qty')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoice.items.map((item) => {
                    const left = remaining(item);
                    const entered = parseReturnQuantity(qty[item.id] ?? '');
                    const invalid = entered === null || entered > left;
                    return (
                      <tr key={item.id}>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-slate-900">{item.productName}</p>
                          <p className="tabular text-xs text-slate-500">{t('returns.paidFor', { amount: amount(item.totalPaise), qty: quantity(item.qtyMilli), unit: unitLabel(item.unit) })}</p>
                        </td>
                        <td className="tabular px-4 py-2.5 text-right text-slate-600">
                          {left > 0 ? `${quantity(left)} ${unitLabel(item.unit)}` : t('returns.fully')}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <input
                            aria-label={t('returns.qtyFor', { name: item.productName })}
                            data-testid="returns-qty"
                            className={`field tabular ml-auto w-24 text-right ${invalid ? 'border-red-400' : ''}`}
                            aria-invalid={invalid}
                            inputMode="decimal"
                            disabled={busy || left === 0}
                            placeholder="0"
                            value={qty[item.id] ?? ''}
                            onChange={(event) => setQty((current) => ({ ...current, [item.id]: event.target.value }))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex flex-wrap items-end justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <label>
                  <span className="mb-1 block text-sm font-medium text-slate-700">{t('returns.reason')}</span>
                  <select className="field" value={reason} disabled={busy} onChange={(event) => setReason(event.target.value as Reason)}>
                    {REASONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {t(r.key)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="text-right">
                  {invalidQty ? (
                    <p className="mb-1 text-sm text-red-700">{t('returns.invalidQty')}</p>
                  ) : tooMany ? (
                    <p className="mb-1 text-sm text-red-700">{t('returns.tooMany')}</p>
                  ) : chosen.length > 0 ? (
                    <p className="mb-1 text-sm text-slate-600">
                      {t('returns.estimate', { amount: amount(estimate) })}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="returns-submit"
                    disabled={busy || chosen.length === 0 || tooMany || invalidQty}
                    onClick={() => void submit()}
                  >
                    {busy ? t('returns.recording') : t('returns.submit')}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The payout and the reduction of unpaid debt are separate instructions for the cashier. */
export function ReturnConfirmation({ result }: { result: ReturnResult }) {
  const t = useT();
  return (
    <div className="panel border-emerald-200 bg-emerald-50 p-4" role="status">
      <p className="font-semibold text-emerald-900" data-testid="returns-success">
        {t('returns.recorded', { number: result.returnNumber })}
      </p>
      <p className="mt-1 text-sm text-emerald-900" data-testid="returns-refund">
        {t('returns.refundDue', { amount: amount(result.cashRefundPaise) })}
      </p>
      {result.debtReductionPaise > 0 ? (
        <p className="mt-1 text-sm text-emerald-900" data-testid="returns-debt-relief">
          {t('returns.debtReduced', { amount: amount(result.debtReductionPaise) })}
        </p>
      ) : null}
      <p className="mt-1 text-sm text-emerald-900">
        {t('returns.value', { amount: amount(result.refundPayablePaise), tax: amount(result.refundTaxPaise) })}
      </p>
    </div>
  );
}
